/**
 * Whether anybody is actually looking.
 *
 * Every decorative animation in the app - the weather, the scene's bloom,
 * an animated name ring, a profile effect - runs for as long as the app is
 * open, whether or not the window is in front of you. That is the whole of
 * the idle power draw: a window left open behind a game is still being
 * composited sixty times a second for nobody.
 *
 * So one attribute on <html> says whether the app is awake, and the
 * stylesheet parks everything decorative when it is not. Nothing functional
 * looks at this: a transition you started still finishes, a spinner on a
 * request still turns. It is only the scenery.
 *
 * Asleep means hidden or not focused. Waking is instant - the attribute
 * flips before the next frame, and a paused animation resumes exactly where
 * it stopped rather than restarting.
 */

let wired = false;

export function trackAwake(): void {
  if (wired || typeof document === 'undefined') return;
  wired = true;

  const sync = () => {
    const awake = !document.hidden && document.hasFocus();
    document.documentElement.dataset.awake = awake ? '1' : '0';
  };

  sync();
  document.addEventListener('visibilitychange', sync);
  window.addEventListener('focus', sync);
  window.addEventListener('blur', sync);
  /* A press inside a window the browser has not told us about yet - it
     happens on the first click back into an Electron window. */
  window.addEventListener('pointerdown', sync, true);
}
